import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, Autocomplete, Alert, CircularProgress } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { addContractLine, type SupplierContractListItem } from '../../api/contracte'
import { listArticles, listUnits } from '../../api/nomenclatoare'
import { errorMessage } from '../../api/client'

const schema = z.object({
  articleId: z.string().min(1, 'Selectați un articol'),
  negotiatedPrice: z.string().min(1, 'Câmp obligatoriu'),
  currency: z.string().min(1, 'Câmp obligatoriu'),
  unitOfMeasureId: z.string().min(1, 'Selectați o UM'),
  validFrom: z.string().min(1, 'Câmp obligatoriu'),
  committedQuantity: z.string().optional(),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = { articleId: '', negotiatedPrice: '', currency: 'RON', unitOfMeasureId: '', validFrom: new Date().toISOString().slice(0, 10), committedQuantity: '' }

export default function AddContractLineDialog({ contract, onClose, onAdded }: {
  contract: SupplierContractListItem | null; onClose: () => void; onAdded: () => void
}) {
  const open = contract !== null
  const { data: articles } = useQuery({ queryKey: ['nomenclatoare', 'articole', 'toate'], queryFn: () => listArticles(), enabled: open })
  const { data: units } = useQuery({ queryKey: ['nomenclatoare', 'um'], queryFn: listUnits, enabled: open })
  const qc = useQueryClient()

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })
  useEffect(() => { if (open) reset(EMPTY) }, [open, reset])

  const mutation = useMutation({
    mutationFn: (values: FormValues) => addContractLine(contract!.id, {
      articleId: values.articleId, negotiatedPrice: Number(values.negotiatedPrice), currency: values.currency,
      unitOfMeasureId: values.unitOfMeasureId, validFrom: values.validFrom,
      committedQuantity: values.committedQuantity ? Number(values.committedQuantity) : null,
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['achizitii', 'contracte'] })
      onAdded()
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>Preț nou pe contractul {contract?.number}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Alert severity="info">Linia veche pentru același articol se închide automat la data de mai jos minus o zi (FR-ACF-015).</Alert>
            <Controller name="articleId" control={control} render={({ field }) => (
              <Autocomplete
                options={articles?.map((a) => a.id) ?? []}
                getOptionLabel={(id) => { const a = articles?.find((x) => x.id === id); return a ? `${a.code} — ${a.shortDescription}` : id }}
                value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                renderInput={(params) => <TextField {...params} label="Articol" error={!!errors.articleId} helperText={errors.articleId?.message} />}
              />
            )} />
            <Stack direction="row" spacing={2}>
              <Controller name="negotiatedPrice" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Preț nou" sx={{ flexGrow: 1 }} error={!!errors.negotiatedPrice} helperText={errors.negotiatedPrice?.message} />
              )} />
              <Controller name="currency" control={control} render={({ field }) => (
                <TextField {...field} label="Monedă" sx={{ width: 100 }} error={!!errors.currency} helperText={errors.currency?.message} />
              )} />
              <Controller name="unitOfMeasureId" control={control} render={({ field }) => (
                <Autocomplete
                  sx={{ width: 130 }} options={units?.map((u) => u.id) ?? []}
                  getOptionLabel={(id) => units?.find((u) => u.id === id)?.code ?? id}
                  value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                  renderInput={(params) => <TextField {...params} label="UM" error={!!errors.unitOfMeasureId} helperText={errors.unitOfMeasureId?.message} />}
                />
              )} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Controller name="validFrom" control={control} render={({ field }) => (
                <TextField {...field} type="date" label="Valabil de la" sx={{ flexGrow: 1 }} slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors.validFrom} helperText={errors.validFrom?.message} />
              )} />
              <Controller name="committedQuantity" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Cant. angajată (opțional)" sx={{ flexGrow: 1 }} />
              )} />
            </Stack>
            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Salvează
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
